import { FC, useEffect, useState } from 'react';
import { Easing, motion, MotionStyle } from 'motion/react';
import { useWindowSize } from '../../custom-hooks/useWindowSize';
import EphemeralMessageStrokeTop from './EphemeralMessageStrokeTop';
import EphemeralMessageStrokeBottom from './EphemeralMessageStrokeBottom';

interface Props {
  delay: number;
  text: React.ReactNode;
  ease: Easing | Easing[];
  duration: number;
  customStyle?: MotionStyle;
  onAnimationEnd?: () => void;
  stroke?: boolean;
  strokePosition?: { top: string | null; right: string | null; left: string | null };
  strokeClassName?: string;
  strokePositionType?: 'top' | 'bottom';
}

const EphemeralMessage: FC<Props> = ({
  delay,
  text,
  customStyle,
  ease,
  onAnimationEnd,
  strokePosition,
  strokeClassName,
  strokePositionType = 'top',
  stroke = true,
}) => {
  const { width } = useWindowSize();
  const [position, setPosition] = useState<{
    top: string | null;
    right: string | null;
    left: string | null;
  }>({
    top: null,
    right: null,
    left: null,
  });
  const [visible, setVisible] = useState<boolean>(true);

  useEffect(() => {
    if (strokePosition) {
      setPosition(strokePosition);
      return;
    }
    if (width < 1024) {
      if (strokePositionType === 'top') setPosition({ top: '-20px', right: null, left: '-4px' });
      if (strokePositionType === 'bottom') setPosition({ top: '115px', right: null, left: '248px' });
    }
    if (width >= 1024) {
      if (strokePositionType === 'top') setPosition({ top: '-20px', right: '225px', left: null });
      if (strokePositionType === 'bottom') setPosition({ top: '115px', right: '-5px', left: null });
    }
  }, [width, strokePosition]);

  return (
    visible && (
      <motion.div
        initial={{ opacity: 0, scale: 0, rotateY: -90 }}
        animate={{ opacity: 1, scale: 1, rotateY: 0 }}
        transition={{
          duration: 1,
          delay,
          ease,
        }}
        style={customStyle}
        onAnimationComplete={() => {
          setTimeout(() => setVisible(false), 5000);
          if (onAnimationEnd) {
            onAnimationEnd();
          }
        }}
        className="tw-max-w-[260px] tw-left-[75px] lg:tw-left-auto tw-top-[315px] lg:tw-top-[245px] lg:tw-right-[115px] xl:tw-right-[171px] tw-border-solid tw-absolute tw-tw-flex tw-flex-col tw-px-4 tw-py-6 tw-bg-stone-0 tw-border-[4px] tw-rounded-3xl tw-border-neutral-100 tw-z-[3]"
      >
        <h3 className="tw-font-atkinson tw-italic tw-text-lg tw-font-normal tw-text-neutral-950 tw-text-center">
          {text}
        </h3>
        {strokePositionType === 'top' && (
          <EphemeralMessageStrokeTop position={position} stroke={stroke} className={strokeClassName} />
        )}
        {strokePositionType === 'bottom' && <EphemeralMessageStrokeBottom position={position} />}
      </motion.div>
    )
  );
};

export default EphemeralMessage;
